<template>
  <b-modal
    :active.sync="isModalActive"
    has-modal-card
    full-screen
    ref="modal"
    :can-cancel="false"
  >
    <div class="modal-card enable-select-text">
      <header class="modal-card-head">
        <h1 class="is-title-header modal-card-title">
          Manual Matching
        </h1>
      </header>
      <section
        class="modal-card-body manual-matching-modal-body"
        style="padding-top: 10px; display: flex; flex-direction: column;"
      >
        <!-- Product Table Container with flex-grow -->
        <div
          class="table-container"
          :style="{ height: tableHeight + 'px', maxHeight: tableHeight + 'px' }"
        >
          <productMappingTable
            :customerId="customerId"
            @selectedRows="handleSelectedProductRows"
            :newTableHeight="tableHeight - 80"
            :matchClicked="matchClickCount"
          ></productMappingTable>
        </div>
        <hr style="margin: 0.5rem" />
        <!-- Transaction Table Container with flex-grow -->
        <div
          class="table-container"
          :style="{ height: tableHeight + 'px', maxHeight: tableHeight + 'px' }"
        >
          <transactionMappingTable
            :customerId="customerId"
            @selectedRows="handleSelectedTransactionRows"
            :newTableHeight="tableHeight - 80"
            :matchClicked="matchClickCount"
          ></transactionMappingTable>
        </div>
      </section>
      <footer class="modal-card-foot" style="padding: 10px 20px;">
        <div style="margin-bottom: 2px; margin-right: 7px;">
          <b-select
            v-model="selectedMatchType"
            placeholder="Match Option"
            style="width: 180px;"
          >
            <option value="Exact">Exact</option>
            <option value="Like for Like">Like for Like</option>
            <option value="Comparable">Comparable</option>
          </b-select>
        </div>
        <b-button
          slot="right"
          type="is-primary"
          @click="matchData(selectedMatchType)"
          :disabled="isMatchDisabled"
        >
          Match
        </b-button>
        <b-button slot="right" type="is-danger" @click="cancel">
          Cancel
        </b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import ProductMappingTable from "./ProductMappingTable.vue";
import TransactionMappingTable from "./TransactionMappingTable.vue";
export default {
  name: "ManualMatching",
  props: {
    isActive: { type: Boolean, default: false },
    customerId: { type: Number, default: -1 }
  },
  components: {
    ProductMappingTable,
    TransactionMappingTable
  },
  data() {
    return {
      form: {
        product: [],
        transaction: []
      },
      isModalActive: false,
      tableHeight: 300, // Default height that will be calculated
      debounceTimeout: null,
      matchClickCount: 0, // Counter to track match button clicks
      selectedMatchType: null
    };
  },
  computed: {
    isMatchDisabled() {
      // Force recalculation by accessing length properties
      const productLength = this.form.product ? this.form.product.length : 0;
      const transactionLength = this.form.transaction
        ? this.form.transaction.length
        : 0;
      return (
        !this.selectedMatchType ||
        productLength === 0 ||
        transactionLength === 0
      );
    }
  },
  methods: {
    // Method to trigger refresh from parent after successful match
    triggerRefresh() {
      this.matchClickCount++;
    },
    handleSelectedProductRows(selectedRows) {
      // Use $set to ensure reactivity
      this.$set(this.form, "product", [...selectedRows]);
    },
    handleSelectedTransactionRows(selectedRows) {
      // Use $set to ensure reactivity
      this.$set(this.form, "transaction", [...selectedRows]);
    },
    matchData(matchType) {
      if (!this.form.product || !this.form.transaction) {
        this.$buefy.dialog.alert({
          title: "Error",
          message: "Please select both product and transaction data to match.",
          type: "is-danger"
        });
        return;
      }
      // Always set matchType from dropdown, fallback to argument if needed
      this.form.matchType = this.selectedMatchType || matchType;
      if (!this.form.matchType) {
        this.$buefy.dialog.alert({
          title: "Error",
          message: "Please select a match type before matching.",
          type: "is-danger"
        });
        return;
      }

      // Save the form data before clearing it
      const dataToSend = { ...this.form };

      // Clear form data and reset match type
      this.form.product = [];
      this.form.transaction = [];
      this.selectedMatchType = null;

      // Emit events with the saved data
      this.$emit("matchedData", dataToSend);

      // Show success message to user
      this.$buefy.toast.open({
        message: "Processing match...",
        type: "is-info",
        duration: 1500
      });
    },
    cancel() {
      this.form = {
        product: [],
        transaction: []
      };
      this.selectedMatchType = null;
      this.$emit("cancel");
    },
    calculateTableHeight() {
      // Get window height
      const windowHeight = window.innerHeight;

      // Calculate available space for tables (modal body)
      // Subtract header (60px), footer (60px), hr (10px), padding (~40px)
      const availableHeight = windowHeight - 170;

      // Divide by 2 for two tables
      this.tableHeight = Math.floor(availableHeight / 2);
    },
    handleResize() {
      // Clear the previous timeout to debounce rapid resize events
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }

      // Set a new timeout
      this.debounceTimeout = setTimeout(() => {
        this.calculateTableHeight();
      }, 200);
    }
  },
  watch: {
    isActive(newValue) {
      this.isModalActive = newValue;

      if (newValue) {
        // When modal becomes active, calculate height
        this.$nextTick(() => {
          this.calculateTableHeight();
        });
      }
    },
    isModalActive(newValue) {
      if (!newValue) {
        this.cancel();
      }
    }
  },
  mounted() {
    // Add resize event listener
    window.addEventListener("resize", this.handleResize);

    // Initial calculation
    this.$nextTick(() => {
      this.calculateTableHeight();
    });
  },
  beforeDestroy() {
    // Remove resize event listener
    window.removeEventListener("resize", this.handleResize);
    // Clear any pending timeouts
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
  }
};
</script>

<style>
.manual-matching-modal-body {
  overflow: hidden !important;
  display: flex;
  flex-direction: column;
}

.table-container {
  overflow: hidden;
  transition: height 0.3s, max-height 0.3s;
}

/* Ensure content fits within the container */
.has-listing-table {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
</style>
